import { MediaObject } from 'src/api/services/MediaClient/MediaClient.types';

/**
 * Whether the restored copy has gone.
 *
 * `restoration_expiry` IS the deadline — it is S3's own expiry-date for the
 * restore, and the placeholder renders it to the customer as "You have {date}
 * to download your content". This used to allow a further 14 days on top of
 * it, which had a file whose copy left the archives bucket a week ago still
 * reading as restored; the back end's ArchivedMediaRestoration::isExpired() is
 * `expires_at->isPast()` and nothing else, so anything more lenient here makes
 * the page offer a download the API then refuses.
 *
 * @param media The MediaObject to check
 * @returns boolean - true if the restored copy's window has closed
 */
export const hasRestorationExpired = (media?: MediaObject): boolean => {
  if (!media?.restoration_expiry) {
    return false;
  }

  const expiry = new Date(media.restoration_expiry).getTime();

  // An unparseable date is NaN, and every comparison against NaN is false —
  // which would read as "not expired" and re-open the gap above. Treat a date
  // we cannot read as gone: the cold-storage copy in the archives bucket is
  // still there, so the customer is pointed at support rather than at a
  // download that fails.
  if (Number.isNaN(expiry)) {
    return true;
  }

  return expiry <= Date.now();
};

/**
 * Determines if a media is ready for download (restoration completed and not expired)
 * @param media The MediaObject to check
 * @returns boolean - true if the media is ready for download
 */
export const isMediaReadyForDownload = (media?: MediaObject): boolean => {
  if (!media) {
    return false;
  }

  return media.restoration_status === 'complete' && !hasRestorationExpired(media);
};

/**
 * Determines if a media is currently being unarchived
 * @param media The MediaObject to check
 * @returns boolean - true if the media is in the process of being unarchived
 */
export const isMediaBeingUnarchived = (media?: MediaObject): boolean => {
  if (!media) {
    return false;
  }

  return !!media.restoration_initialized_at && media.restoration_status !== 'complete';
};

/**
 * Determines if a media needs unarchiving, either because it was never unarchived
 * or because its restoration has expired
 * @param media The MediaObject to check
 * @param videoIsArchived Boolean indicating if the video is archived
 * @returns boolean - true if the media needs to be unarchived
 */
export const isMediaNeedsUnarchiving = (media?: MediaObject, videoIsArchived = false): boolean => {
  if (!media) {
    return false;
  }

  const isExpiredRestoration =
    media.restoration_status === 'complete' && hasRestorationExpired(media);

  return videoIsArchived || isExpiredRestoration;
};
